import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { User, Mail, Phone, CalendarDays, ShoppingBag, IndianRupee, ExternalLink } from "lucide-react";

/** "Customer Details" tab on the order detail page — profile + full order history. */
export const OrderCustomerPanel = ({ orderId, detailBase }) => {
    const [c, setC] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/orders-staff/${orderId}/customer`)
            .then((r) => setC(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Could not load the customer profile"));
    }, [orderId]);

    if (error) return <div className="bg-white border border-stone-200 p-6 text-sm text-red-700" data-testid="customer-panel-error">{error}</div>;
    if (!c) return <div className="text-sm text-stone-500 p-6" data-testid="customer-panel-loading">Loading customer…</div>;

    return (
        <div className="space-y-4" data-testid="customer-details-panel">
            {!c.account_found && (
                <div className="bg-amber-50 border border-amber-300 px-4 py-3 text-xs text-amber-900" data-testid="customer-no-account">
                    No registered account is linked to this order — showing the details captured at checkout.
                </div>
            )}

            <div className="bg-white border border-stone-200 p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Profile</div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    <Field icon={User} label="Name" value={c.name || "—"} testId="cust-name" />
                    <Field icon={Mail} label="Email" value={c.email || "—"} mono testId="cust-email" />
                    <Field icon={Phone} label="Phone" value={c.phone || "—"} mono testId="cust-phone" />
                    <Field
                        icon={CalendarDays}
                        label="Registered"
                        value={c.registered_at ? new Date(c.registered_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        testId="cust-registered"
                    />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <Stat icon={ShoppingBag} label="Total orders placed" value={c.total_orders} testId="cust-total-orders" />
                <Stat icon={IndianRupee} label="Total amount spent" value={formatINR(c.total_spent)} testId="cust-total-spent" />
            </div>

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <div className="px-4 py-3 border-b border-stone-100 text-xs uppercase tracking-widest text-stone-500">
                    Order history ({c.orders.length})
                </div>
                {c.orders.length === 0 ? (
                    <div className="p-6 text-sm text-stone-500" data-testid="cust-no-orders">No orders on record.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="cust-orders-table">
                        <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                            <tr>
                                <th className="p-3">Order ID</th>
                                <th className="p-3">Date</th>
                                <th className="p-3">Status</th>
                                <th className="p-3 text-right">Amount</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {c.orders.map((o) => (
                                <tr
                                    key={o.id}
                                    className={`border-t border-stone-100 hover:bg-sky-50/50 transition-colors ${o.id === orderId ? "bg-sky-50" : ""}`}
                                    data-testid={`cust-order-${o.id}`}
                                >
                                    <td className="p-3">
                                        <Link to={`${detailBase}/${o.id}`} className="font-mono text-xs text-sky-700 hover:underline" data-testid={`cust-order-link-${o.id}`}>
                                            #{o.reference}
                                        </Link>
                                        {o.id === orderId && <span className="ml-2 text-[10px] uppercase tracking-widest text-sky-700">viewing</span>}
                                    </td>
                                    <td className="p-3 text-xs text-stone-600">
                                        {o.date ? new Date(o.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                    </td>
                                    <td className="p-3">
                                        <span className="text-[10px] uppercase tracking-widest border border-stone-300 text-stone-600 px-1.5 py-0.5">{o.status}</span>
                                        {o.payment_status === "paid" && <span className="ml-1 text-[10px] uppercase tracking-widest border border-emerald-400 text-emerald-700 px-1.5 py-0.5">paid</span>}
                                    </td>
                                    <td className="p-3 text-right font-mono">{formatINR(o.amount)}</td>
                                    <td className="p-3 text-right">
                                        <Link to={`${detailBase}/${o.id}`} className="text-sky-700 inline-flex" aria-label="Open order">
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

const Field = ({ icon: Icon, label, value, mono = false, testId }) => (
    <div className="flex items-start gap-2.5">
        <Icon className="w-3.5 h-3.5 text-stone-400 mt-1 shrink-0" />
        <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`text-stone-900 ${mono ? "font-mono text-xs" : "text-sm"}`} data-testid={testId}>{value}</div>
        </div>
    </div>
);

const Stat = ({ icon: Icon, label, value, testId }) => (
    <div className="bg-white border border-stone-200 p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
            <Icon className="w-3.5 h-3.5" />{label}
        </div>
        <div className="font-serif text-3xl text-stone-900 mt-1" data-testid={testId}>{value}</div>
    </div>
);
